import { useState } from 'react';
import { motion } from 'framer-motion';
import { Timer, ArrowLeft, ArrowRight } from 'lucide-react';
import { useTastingStore } from '@/stores/tastingStore';
import { FINISH_DESCRIPTORS } from '@/types';
import RatingInput from '@/components/shared/RatingInput';
import DescriptorPicker from '@/components/shared/DescriptorPicker';

export default function FinishStep() {
  const {
    getCurrentSpirit,
    currentSpiritIndex,
    updateSpirit,
    previousStep,
    nextStep,
  } = useTastingStore();

  const spirit = getCurrentSpirit();
  const [rating, setRating] = useState(spirit?.finish_rating || 0);
  const [notes, setNotes] = useState(spirit?.finish_notes || '');
  const [descriptors, setDescriptors] = useState<string[]>(spirit?.finish_descriptors || []);

  const handleSave = () => {
    updateSpirit(currentSpiritIndex, {
      finish_rating: rating,
      finish_notes: notes || undefined,
      finish_descriptors: descriptors.length > 0 ? descriptors : undefined,
    });
    nextStep();
  };

  if (!spirit) {
    return (
      <div className="text-center py-12">
        <p className="text-dark-400">Keine Spirituose ausgewählt</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <div className="badge-primary mb-4">
          Spirituose {currentSpiritIndex + 1}
        </div>
        <h1 className="text-2xl md:text-3xl font-display font-bold text-dark-50 mb-2">
          {spirit.spirit_name}
        </h1>
        {spirit.spirit_brand && (
          <p className="text-dark-400">{spirit.spirit_brand}</p>
        )}
      </div>

      {/* Step Title */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center"
      >
        <div className="w-16 h-16 bg-primary-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <Timer className="w-8 h-8 text-primary-500" />
        </div>
        <h2 className="text-xl font-semibold text-dark-100 mb-2">Abgang</h2>
        <p className="text-dark-400 text-sm max-w-md mx-auto">
          Achte auf den Nachgeschmack. Wie lange bleibt das Aroma?
          Wie fühlt sich der Abgang an?
        </p>
      </motion.div>

      {/* Rating */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="card"
      >
        <h3 className="text-lg font-semibold text-dark-100 mb-4">Bewertung</h3>
        <RatingInput value={rating} onChange={setRating} />
      </motion.div>

      {/* Descriptors */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="card"
      >
        <h3 className="text-lg font-semibold text-dark-100 mb-4">Charakter (optional)</h3>
        <DescriptorPicker
          categories={FINISH_DESCRIPTORS}
          selected={descriptors}
          onChange={setDescriptors}
        />
      </motion.div>

      {/* Notes */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="card"
      >
        <h3 className="text-lg font-semibold text-dark-100 mb-4">Notizen (optional)</h3>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Beschreibe den Abgang in deinen eigenen Worten..."
          className="input min-h-[100px] resize-none"
        />
      </motion.div>

      {/* Navigation */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="flex gap-3"
      >
        <button
          onClick={previousStep}
          className="btn-secondary flex items-center gap-2"
        >
          <ArrowLeft className="w-5 h-5" />
          Zurück
        </button>
        <button
          onClick={handleSave}
          disabled={rating === 0}
          className="btn-primary flex-1 flex items-center justify-center gap-2"
        >
          Weiter
          <ArrowRight className="w-5 h-5" />
        </button>
      </motion.div>
    </div>
  );
}
